import AdministrativeTransactionRequest, { AdministrativeTransactionRequestResponse, GetMyRequestsReqDto } from "../../interfaces/adminRequests/AdministrativeTransactionRequest";
import {CreateAdministrativeTransactionRequest} from "../../interfaces/adminRequests/CreateAdministrativeTransactionRequest";
import { TransactionReasonResponseDto } from "../../interfaces/adminRequests/ReasonTypes";
import { ApiResponse, PagedResults } from "../../interfaces/genericResponse/GenericResponse";
import { ManagerRequest } from "../../interfaces/UserInfo";
import { buildQuery } from "../../utilities/common/buildQueryParams";

interface ApiErrorResponse {
  error: string;
  details?: any;
}

// Your existing TimeOff-specific interfaces (keep these if the "Add New Request" modal will still handle TimeOff submission)
export interface TimeOffRequestFrontend {
  userId: string;
  timeOffStartTime: string;
  timeOffEndTime: string;
  isApproved: boolean;
  typeOfLeave: number;
  isRejected: boolean;
  comment?: string;
  reason?: string;
}

export interface TimeOffPayloadBackend {
  id: number;
  userId: string;
  typeOfLeave: number;
  comment?: string;
  isApproved: boolean;
  timeOffStartTime: string;
  timeOffEndTime: string;
}

// Function to fetch administrative transaction requests for a user
export async function getAdministrativeTransactionRequestsForUser(
  authToken: string,
  userId: string
): Promise<AdministrativeTransactionRequest[] | null> {
  try {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/admin/request`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error("Error fetching administrative transaction requests:", response.statusText);
      return null;
    }

    const res = await response.json();

    const requests: AdministrativeTransactionRequest[] = res.map((request: any) => ({
      id: request.id,
      requesterName: request.requesterName,
      approverName: request.approverName || null,
      transactionType: request.transactionType,
      status: request.status,
      requestTime: request.requestTime,
      taskId: request.taskId,
      templateTaskId: request.templateTaskId, 
      taskTitle: request.taskTitle,
      completionTime: request.completionTime || null,
    }));

    return requests;
  } catch (error) {
    console.error("Error fetching administrative transaction requests:", error);
    return null;
  }
}

export async function getMyAdminRequests(
  authToken: string,
  req: GetMyRequestsReqDto
): Promise<ApiResponse<PagedResults<AdministrativeTransactionRequest>>> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/request/self`;

    // Build query string
    const queryString = buildQuery(req);

    const response = await fetch(`${apiUrl}?${queryString}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}


// Function to create a new administrative transaction request
export async function createAdministrativeTransactionRequest(
  authToken: string,
  newRequest: CreateAdministrativeTransactionRequest
): Promise<AdministrativeTransactionRequest | null> {
  try {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/admin/request`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newRequest),
    });

    if (!response.ok) {
      console.error("Error creating administrative transaction request:", response.statusText);
      return null;
    }

    const res = await response.json();

    const createdRequest: AdministrativeTransactionRequest = {
      id: res.id,
      requesterName: res.requesterName,
      approverName: res.approverName || null,
      transactionType: res.transactionType,
      status: res.status,
      requestTime: res.requestTime,
      completionTime: res.completionTime || null,
      taskId: res.taskId || null,
      templateTaskId: res.templateTaskId || null,
      taskTitle: res.taskTitle || null
    };

    return createdRequest;
  } catch (error) {
    console.error("Error creating administrative transaction request:", error);
    return null;
  } 
}

// Function to approve an existing administrative transaction request
export async function approveAdministrativeTransactionRequest(
    authToken: string,
    requestId: number
): Promise<ApiResponse<boolean>> {
    try {
        const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/admin/request/approve/${requestId}`;

        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        return await response.json();
    } catch (error) {
        console.error("Error approving administrative transaction request:", error);
        throw error;
    }
}

// Function to reject an existing administrative transaction request
export async function rejectAdministrativeTransactionRequest(
  authToken: string,
  requestId: number
): Promise<ApiResponse<boolean>> {
  try {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/admin/request/reject/${requestId}`;

    const response = await fetch(apiUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

   return await response.json();
  } catch (error) {
    console.error("Error rejecting administrative transaction request:", error);
    throw error;
  }
}

/**
 * Fetches a list of administrative transaction requests awaiting approval for the current user.
 * This function is intended for Supervisors, Program Managers, and Owners.
 *
 * @param idToken The authentication token of the current user.
 * @returns A Promise resolving to an array of AdministrativeTransactionRequestResponse or an ApiErrorResponse.
 */
export async function getPendingAdministrativeRequests(
  idToken: string,
   transactionalTypeName :string = "",
  pageNumber : number =1,
  pageSize : number = 10
): Promise<ApiResponse<PagedResults<ManagerRequest>>> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/admin/request/pending-approvals?transactionalTypeName=${encodeURIComponent(transactionalTypeName)}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });

   var res = await response.json();
   return res;

  } catch (error: any) {
    console.error("Error in getPendingAdministrativeRequests (network/fetch error):", error);
    throw error;
  }
}

/**
 * Fetches a list of predefined reasons from the backend.
 *
 * @param authToken The authentication token.
 * @param reasonType The type of reasons to fetch (e.g., 'CLOSURE', 'EXTENSION', 'LATE_CLOSURE').
 * @returns A promise resolving to an array of TransactionReasonResponseDto or an ApiErrorResponse.
 */
export async function getReasonsByType(
  authToken: string,
  reasonType: string
): Promise<TransactionReasonResponseDto[] | ApiErrorResponse> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/lookup/reasons?type=${encodeURIComponent(reasonType)}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorBody = await response.json();
      return {
        error: errorBody.error || `Failed to fetch reasons of type ${reasonType}`,
        details: errorBody.details || errorBody,
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error(`Error in getReasonsByType (${reasonType}):`, error);
    return {
      error: `Failed to fetch reasons for ${reasonType}.`,
      details: error.message || error,
    };
  }
}


export interface JustificationReason {
  id: number;
  reasonText: string;
  isActive: boolean;
}

export async function getJustificationReasons(
  idToken: string
): Promise<JustificationReason[] | ApiResponse<null>> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TaskJustification/get-justification-reasons`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return {
        data: null,
        statusCode: response.status,
        message: `Error: ${response.statusText}`,
        success: false,
      };
    }

    const result: ApiResponse<JustificationReason[]> = await response.json();
    return result.data;
  } catch (error: any) {
    return {
      data: null,
      statusCode: 500,
      message: error.message || "Failed to fetch justification reasons.",
      success: false,
    };
  }
}


export interface TaskJustificationRequest {
  taskId: number;
  justificationType: string; // "LATE" or "AT-RISK"
  reasonLookupId: number;
  detailsText: string;
}

export async function addTaskJustification(
  idToken: string,
  payload: TaskJustificationRequest
): Promise<any | ApiErrorResponse> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TaskJustification/add-justification`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      return {
        error: errorBody.error || "Unknown error adding justification",
        details: errorBody,
      };
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error("Error in addTaskJustification:", error);
    return {
      error: "Failed to add justification. Please check your network connection.",
      details: error.message || error,
    };
  }
}